import React, { useMemo, useState } from 'react';
import { Badge, Button, Empty, Input, List, Modal, Space, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { getString } from '../utils/translations';
import { getSelectableVideoOrganizations } from '../utils/videoProcessingAccess';

const { Text } = Typography;

const OrganizationSelectModal = ({ open, closable = false, onClose }) => {
  const {
    organizations,
    organization,
    selectingOrganization,
    selectOrganization,
    signOut,
  } = useAuth();
  const [search, setSearch] = useState('');

  const filteredOrganizations = useMemo(() => {
    const query = search.trim().toLowerCase();
    const selectableOrganizations = getSelectableVideoOrganizations(organizations);
    if (!query) return selectableOrganizations;
    return selectableOrganizations.filter(org => (org.name || '').toLowerCase().includes(query));
  }, [organizations, search]);

  const handleSelect = async (org) => {
    if (!org) return;
    const success = await selectOrganization(org.id);
    if (success && onClose) {
      onClose();
    }
  };

  return (
    <Modal
      title={getString('pipeline.auth.select_organization', 'Select Organization')}
      open={open}
      closable={closable}
      maskClosable={false}
      keyboard={closable}
      onCancel={onClose}
      footer={[
        <Button key="sign-out" onClick={signOut}>
          {getString('pipeline.nav.sign_out', 'Sign Out')}
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text type="secondary">
          {getString(
            'pipeline.auth.select_organization_desc',
            'Choose the organization you want to use for this video analysis session.'
          )}
        </Text>

        {filteredOrganizations.length > 5 && (
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={getString('pipeline.auth.search_organizations', 'Search organizations')}
            allowClear
          />
        )}

        {filteredOrganizations.length === 0 ? (
          <Empty description={getString('pipeline.auth.no_organizations_found', 'No organizations found')} />
        ) : (
          <List
            dataSource={filteredOrganizations}
            renderItem={(org) => {
              const isCurrent = organization?.id === org.id;

              return (
                <List.Item
                  actions={[
                    <Button
                      key="select"
                      type={isCurrent ? 'default' : 'primary'}
                      loading={selectingOrganization}
                      onClick={() => handleSelect(org)}
                    >
                      {isCurrent
                        ? getString('pipeline.auth.current_organization', 'Current')
                        : getString('pipeline.auth.select', 'Select')}
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={<TeamOutlined style={{ color: '#1890ff', fontSize: 20 }} />}
                    title={org.name}
                    description={
                      <Badge
                        status={org.videoProcessingWriteEnabled === false ? 'warning' : 'success'}
                        text={org.videoProcessingWriteEnabled === false
                          ? getString('pipeline.auth.video_processing_read_only', 'Video processing read-only')
                          : getString('pipeline.auth.video_processing_enabled', 'Video processing enabled')}
                      />
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Space>
    </Modal>
  );
};

export default OrganizationSelectModal;
